import { createContext, useContext, type ReactNode } from 'react';
import type { OpenapiQueryClient } from 'openapi-react-query';
import type { Client } from 'openapi-fetch';
import type { LegacyAuthGatewayPaths } from './legacyAuthGatewayClient';

type LegacyAuthGatewayRqClient = OpenapiQueryClient<LegacyAuthGatewayPaths>;
type LegacyAuthGatewayFetchClient = Client<LegacyAuthGatewayPaths>;

interface LegacyAuthGatewayContextValue {
  rqClient: LegacyAuthGatewayRqClient;
  fetchClient: LegacyAuthGatewayFetchClient;
}

const LegacyAuthGatewayApiClientContext =
  createContext<LegacyAuthGatewayContextValue | null>(null);

export interface LegacyAuthGatewayApiClientProviderProps {
  rqClient: LegacyAuthGatewayRqClient;
  fetchClient: LegacyAuthGatewayFetchClient;
  children: ReactNode;
}

/**
 * Provider for the Legacy Auth Gateway API Client.
 * Wraps the application to provide access to the API client hooks.
 */
export const LegacyAuthGatewayApiClientProvider = ({
  rqClient,
  fetchClient,
  children,
}: LegacyAuthGatewayApiClientProviderProps) => {
  return (
    <LegacyAuthGatewayApiClientContext.Provider
      value={{ rqClient, fetchClient }}
    >
      {children}
    </LegacyAuthGatewayApiClientContext.Provider>
  );
};

/**
 * Hook to access the Legacy Auth Gateway API Client.
 */
export const useLegacyAuthGatewayRq = () => {
  const context = useContext(LegacyAuthGatewayApiClientContext);
  if (!context) {
    throw new Error(
      'useLegacyAuthGatewayRq must be used within a LegacyAuthGatewayApiClientProvider',
    );
  }
  return context.rqClient;
};

/**
 * Hook to access the Legacy Auth Gateway Fetch Client.
 */
export const useLegacyAuthGatewayFetch = () => {
  const context = useContext(LegacyAuthGatewayApiClientContext);
  if (!context) {
    throw new Error(
      'useLegacyAuthGatewayFetch must be used within a LegacyAuthGatewayApiClientProvider',
    );
  }
  return context.fetchClient;
};
